/* eslint-disable @typescript-eslint/no-useless-constructor */
import { SupabaseClient } from '@supabase/supabase-js';
import { StudentProfile } from '@grcs/types/DataTypes';
import { BaseRepository } from '../BaseRepository';
import { DatabaseArrayResult, TABLE_NAMES } from '../types';

/**
 * Student repository that handles all student-related database operations
 * Preserves all existing function signatures and business logic
 */
export class StudentRepository extends BaseRepository {
  constructor(supabase: SupabaseClient<any, 'public', any>) {
    super(supabase);
  }

  /**
   * Preserves exact logic from getStudentInfoByStudentId
   */
  async getStudentInfoByStudentId(
    studentId: string,
  ): Promise<DatabaseArrayResult<StudentProfile>> {
    if (!studentId) throw new Error('Student id is not defined');
    return this.getDataById(TABLE_NAMES.STUDENT, 'student_token', studentId);
  }

  /**
   * Preserves exact logic from getStudentsByClassId
   */
  async getStudentsByClassId(
    classId: string,
  ): Promise<DatabaseArrayResult<StudentProfile>> {
    if (!classId) throw new Error('Class id is not defined');
    return this.getDataById(TABLE_NAMES.STUDENT, 'class_token', classId);
  }

  /**
   * Preserves exact logic from addStudentToClass
   * Temporary fix to recover the student data
   */
  async addStudentToClass(
    studentUserId: string,
    studentFirstName: string,
    studentLastName: string,
    studentEmail: string,
    classId: string,
    className: string,
  ): Promise<DatabaseArrayResult<any>> {
    return this.updateDataById(
      TABLE_NAMES.STUDENT,
      'student_token',
      studentUserId,
      {
        first_name: studentFirstName,
        last_name: studentLastName,
        email: studentEmail,
        class_token: classId,
        class_name: className,
        deleted: false,
      },
    );
  }

  /**
   * Preserves exact logic from getAllStudents
   */
  async getAllStudents(): Promise<DatabaseArrayResult<StudentProfile>> {
    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.STUDENT)
        .select('*');

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Preserves exact logic from getAllStudentsByClassId
   */
  async getAllStudentsByClassId(
    classId: string,
  ): Promise<DatabaseArrayResult<StudentProfile>> {
    if (!classId) throw new Error('Class id is not defined');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.STUDENT)
        .select('*')
        .eq('class_token', classId);

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Preserves exact logic from addNewStudentToStudentDB
   */
  async addNewStudentToStudentDB(
    userToken: string,
    firstName: string,
    lastName: string,
    email: string,
    semester: string,
  ): Promise<DatabaseArrayResult<any>> {
    return this.insertData(TABLE_NAMES.STUDENT, {
      student_token: userToken,
      first_name: firstName,
      last_name: lastName,
      email: email,
      semester_year: semester,
    });
  }

  /**
   * Get student by semester year - essential for semester-specific operations
   */
  async getStudentBySemester(
    studentToken: string,
    semesterYear: string,
  ): Promise<DatabaseArrayResult<StudentProfile>> {
    if (!studentToken) throw new Error('Student token is required');
    if (!semesterYear) throw new Error('Semester year is required');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.STUDENT)
        .select('*')
        .eq('student_token', studentToken)
        .eq('semester_year', semesterYear)
        .eq('deleted', false);

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Re-enroll existing student for current semester
   * Moves the existing enrollment to the current semester with fresh progress tracking
   */
  async reenrollStudentForCurrentSemester(
    studentToken: string,
    semesterYear: string,
    firstName: string,
    lastName: string,
    email: string,
    classId?: string,
    className?: string,
    previousSemester?: string,
  ): Promise<DatabaseArrayResult<StudentProfile>> {
    if (!studentToken) throw new Error('Student token is required');
    if (!semesterYear) throw new Error('Semester year is required');

    if (!previousSemester) throw new Error('Previous semester is required');
    const { data, error } = await this.supabase
      .from(TABLE_NAMES.STUDENT)
      .update({
        semester_year: semesterYear,
        first_name: firstName,
        last_name: lastName,
        email: email,
        class_token: classId || '',
        class_name: className || '',
        todo_article_tokens: [], // Fresh start for new semester
        pending_article_tokens: [], // No in-progress work
        complete_article_tokens: [], // Clean progress tracking
        deleted: false,
        phone_number: '',
      })
      .eq('student_token', studentToken)
      .eq('semester_year', previousSemester)
      .eq('deleted', false)
      .select();
    return { data, error };
  }

  /**
   * Update student information for current semester
   * Used when student exists in current semester but needs info updates
   */
  async updateStudentSemesterInfo(
    studentToken: string,
    semesterYear: string,
    updateData: Partial<StudentProfile>,
  ): Promise<DatabaseArrayResult<StudentProfile>> {
    if (!studentToken) throw new Error('Student token is required');
    if (!semesterYear) throw new Error('Semester year is required');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.STUDENT)
        .update(updateData)
        .eq('student_token', studentToken)
        .eq('semester_year', semesterYear)
        .eq('deleted', false)
        .select();

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Generic method to insert data
   */
  async insertData(
    tableName: string,
    data: any,
  ): Promise<DatabaseArrayResult<any>> {
    return super.insertData(tableName, data);
  }

  /**
   * Generic method to update data by ID
   */
  async updateDataById(
    tableName: string,
    idName: string,
    idValue: string,
    updateData: any,
  ): Promise<DatabaseArrayResult<any>> {
    return super.updateDataById(tableName, idName, idValue, updateData);
  }

  /**
   * Generic method to get data by ID
   */
  async getDataById(
    tableName: string,
    idName: string,
    idValue: string,
    excludeDeleted = false,
  ): Promise<DatabaseArrayResult<any>> {
    return super.getDataById(tableName, idName, idValue, excludeDeleted);
  }
}
